import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Download, Award, BookOpen, Factory, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';

const documents = [
  {
    title: "Company Profile",
    description: "Complete overview of Strawberry Studio Exports, our facilities, capabilities, and certifications.",
    icon: BookOpen,
    url: "https://customer-assets.emergentagent.com/job_981a21ab-2b01-463f-b2b0-9581b9124d66/artifacts/815mmnhq_Strawberry%20Profile.pdf",
    category: "Profile",
    size: "10.5 MB",
  },
  {
    title: "GOTS Certificate",
    description: "Global Organic Textile Standard certification for organic garment production and supply chain.",
    icon: Award,
    url: "https://customer-assets.emergentagent.com/job_981a21ab-2b01-463f-b2b0-9581b9124d66/artifacts/owtd2ulm_Strawberry%20Studio%20Exports%20Pvt.%20Ltd._GOTS%20SC-012216-0%5B9%5D.pdf",
    category: "Certificate",
    size: "0.6 MB",
  },
  {
    title: "Oeko-Tex Certificate",
    description: "Standard 100 certification by AITEX ensuring harmful substance-free textiles.",
    icon: Award,
    url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_55d77140-fd60-4d45-aecb-29fe8f5dc66a/artifacts/icf15z44_2021OK2754_STRAWBERRY%20STUDIO%20EXPORTS.pdf",
    category: "Certificate",
    size: "0.07 MB",
  },
  {
    title: "Girls' Collection Catalogue",
    description: "Complete lookbook of our girls' wear collection including dresses, blouses, skirts, and more.",
    icon: FileText,
    url: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/2mocs5gf_Girls%20CATALOGUE.pdf",
    category: "Catalogue",
    size: "PDF",
  },
  {
    title: "Boys' Collection Catalogue",
    description: "Complete lookbook of our boys' wear collection including shirts, shorts, trousers, and outerwear.",
    icon: FileText,
    url: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/hhpuqnpc_Boys%20Catalogue%20x2.pdf",
    category: "Catalogue",
    size: "PDF",
  },
  {
    title: "Laundry Capabilities",
    description: "Details of our in-house laundry facility including garment dyeing and denim washing.",
    icon: Factory,
    url: "https://customer-assets.emergentagent.com/job_981a21ab-2b01-463f-b2b0-9581b9124d66/artifacts/qgtnxuhb_Laundry.pdf",
    category: "Facilities",
    size: "0.84 MB",
  },
  {
    title: "ProShield Training",
    description: "Worker training documentation for health, safety, and protective equipment protocols.",
    icon: GraduationCap,
    url: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/an91q6gq_PROSHIELD%20TRANING.pdf",
    category: "Training",
    size: "PDF",
  },
  {
    title: "E.T.I Training",
    description: "Ethical Trade Initiative training for workers on ethical labor practices and standards.",
    icon: GraduationCap,
    url: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/m1m2zyl1_E.T.I%20TRANING.pdf",
    category: "Training",
    size: "PDF",
  },
  {
    title: "General Awareness Training",
    description: "Samanay Jagrukta - General awareness training for workforce development and compliance.",
    icon: GraduationCap,
    url: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/z24jwqoh_SAMANAY%20JAGRUKTA%20TRANING.pdf",
    category: "Training",
    size: "PDF",
  },
  {
    title: "Training Schedule",
    description: "Annual training calendar and schedule for worker development programs and compliance sessions.",
    icon: GraduationCap,
    url: "https://customer-assets.emergentagent.com/job_fashion-exporter/artifacts/8oqu3mv2_Traning.pdf",
    category: "Training",
    size: "PDF",
  },
];

const ResourcesSection = () => {
  return (
    <section 
      id="resources"
      data-testid="resources-section"
      className="py-24 md:py-32 bg-white border-t border-slate-100"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-16"
        >
          <p className="font-sans text-sm font-medium tracking-[0.3em] uppercase text-slate-400 mb-4">
            Resources
          </p>
          <h2 className="font-serif text-4xl md:text-5xl font-normal tracking-tight text-slate-900 mb-6">
            Download Our<br />
            <span className="text-rose-700">Company Documents</span>
          </h2>
          <p className="font-sans text-lg text-slate-600 max-w-2xl mx-auto">
            Access our company profile, product catalogues, certificates, and facility information 
            for your reference.
          </p>
        </motion.div>

        {/* Documents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {documents.map((doc, index) => (
            <motion.a
              key={index}
              href={doc.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group bg-slate-50 hover:bg-white border border-transparent hover:border-slate-200 p-8 transition-all duration-500 hover:shadow-xl hover:shadow-slate-200/50 cursor-pointer"
              data-testid={`resource-card-${index}`}
            >
              {/* Category Badge */}
              <span className="inline-block font-sans text-xs font-medium tracking-wider uppercase text-rose-700 bg-rose-50 px-3 py-1 mb-4">
                {doc.category}
              </span>
              
              {/* Icon */}
              <div className="w-14 h-14 bg-slate-900 flex items-center justify-center mb-4 group-hover:bg-rose-700 transition-colors duration-300">
                <doc.icon size={28} className="text-white" strokeWidth={1.5} />
              </div>
              
              {/* Content */}
              <h3 className="font-serif text-xl text-slate-900 mb-2">
                {doc.title}
              </h3>
              <p className="font-sans text-sm text-slate-600 leading-relaxed mb-4">
                {doc.description}
              </p>
              
              {/* Download Info */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                <span className="font-sans text-xs text-slate-400 uppercase tracking-wider">
                  PDF • {doc.size}
                </span>
                <div className="flex items-center gap-2 text-rose-700 group-hover:text-rose-800">
                  <span className="font-sans text-sm font-medium">Download</span>
                  <Download size={16} strokeWidth={2} />
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-12 text-center"
        >
          <p className="font-sans text-sm text-slate-500">
            Need additional certificates or documents? <button onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })} className="text-rose-700 hover:underline font-medium">Contact us</button> for specific requirements.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ResourcesSection;
